import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, {API, apiDownload, runAiJob } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader } from "../components/PageHeader";
import AltriClientiPicker, { nomiCommittenti } from "../components/AltriClientiPicker";
import { ClientePicker } from "../components/ClientePicker";
import { useNuovoParam } from "../lib/useNuovoParam";
import { AltriDettagli } from "../components/AltriDettagli";
import { TabsPreventiviAvvisi } from "../components/TabsPreventiviAvvisi";
import { StatusBadge } from "../components/Badges";
import SignaturePad from "../components/SignaturePad";
import { Plus, Trash, PencilSimple, DownloadSimple, PaperPlaneTilt, CheckCircle, FileArrowUp, CurrencyEur, SquaresFour, List as ListIcon, User, Copy, Sparkle, FileText, PenNib } from "@phosphor-icons/react";
import { eur } from "../lib/impresa";

const fmt = eur;

const emptyVoce = () => ({ titolo: "", descrizione: "", onorario: 0, spese: 0 });
const emptyPrev = () => ({
  numero: "", data: new Date().toISOString().slice(0, 10), cliente_id: "", altri_clienti_ids: [],
  tipo_pratica: "generica",
  descrizione: "",
  voci: [emptyVoce()],
  cassa_pct: 5.0,
  spese_forfait: 0,
  spese_esenti: 0,
  marca_bollo: 2,
  banca: "",
  intestatario_conto: "",
  iban: "",
  importo: 0,
  stato: "bozza",
  note: "",
});

const computeTotali = (form) => {
  const voci = form.voci || [];
  const tot_onorario = voci.reduce((s, v) => s + (parseFloat(v.onorario) || 0), 0);
  const tot_spese_voci = voci.reduce((s, v) => s + (parseFloat(v.spese) || 0), 0);
  const spese_forfait = parseFloat(form.spese_forfait) || 0;
  const tot_spese = tot_spese_voci + spese_forfait;
  const cassa_pct = parseFloat(form.cassa_pct) || 0;
  const cassa_val = Math.round(tot_onorario * cassa_pct) / 100;
  const marca_bollo = parseFloat(form.marca_bollo) || 0;
  const spese_esenti = parseFloat(form.spese_esenti) || 0;
  const totale = Math.round((tot_onorario + tot_spese + cassa_val + marca_bollo + spese_esenti) * 100) / 100;
  return { tot_onorario, tot_spese_voci, spese_forfait, spese_esenti, tot_spese, cassa_val, marca_bollo, totale };
};

export default function Preventivi() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [tipi, setTipi] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPrev());
  const [uploadDialog, setUploadDialog] = useState(null);
  const [padOpen, setPadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef(null);

  const [macroaree, setMacroaree] = useState([]);
  const [selectedMacroarea, setSelectedMacroarea] = useState("");

  const load = async () => {
    const [p, c, t, m] = await Promise.all([
      api.get("/preventivi"),
      api.get("/clienti"),
      api.get("/settings/tipi-incarico"),
      api.get("/settings/macroaree"),
    ]);
    setItems(p.data);
    setClienti(c.data);
    setTipi(t.data);
    setMacroaree(m.data);
  };
  useEffect(() => { load(); }, []);

  const clientName = (id) => {
    const c = clienti.find((x) => x.id === id);
    if (!c) return "—";
    if (c.ragione_sociale) return c.ragione_sociale;
    const parts = [c.cognome, c.nome].filter(Boolean);
    return parts.length ? parts.join(" ") : "—";
  };
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("preventivi.view") || "cards");
  const changeViewMode = (m) => { setViewMode(m); try { localStorage.setItem("preventivi.view", m); } catch { /* ignore */ } };
  const tipoLabel = (tipo) => tipi.find((t) => t.tipo === tipo)?.label || tipo;
  const macroareaLabel = (slug) => (macroaree.find((m) => m.slug === slug)?.label) || "—";
  const macroareaForTipo = (slug) => tipi.find((t) => t.tipo === slug)?.macroarea || "altro";

  const onTipoChange = (v) => {
    setForm((prev) => ({ ...prev, tipo_pratica: v }));
  };

  const currentTipo = () => tipi.find((t) => t.tipo === form.tipo_pratica);
  const [aiLoading, setAiLoading] = useState(false);

  const addVoceDaTipo = (t) => {
    if (!t) return;
    setForm((prev) => {
      const voci = [...(prev.voci || [])];
      const isFirstEmpty = voci.length === 1 && !voci[0].titolo && !voci[0].descrizione && !voci[0].onorario && !voci[0].spese;
      const newVoce = {
        titolo: t.label || "",
        descrizione: t.descrizione || "",
        onorario: t.prezzo_standard || 0,
        spese: t.spese_standard || 0,
      };
      if (isFirstEmpty) voci[0] = newVoce;
      else voci.push(newVoce);
      return { ...prev, voci };
    });
    toast.success(`Voce "${t.label}" aggiunta`);
  };

  const generaPrestazioniAI = async () => {
    setAiLoading(true);
    try {
      const data = await runAiJob("prestazioni-preventivo", {
        tipo_incarico: form.tipo_pratica,
        descrizione: form.descrizione,
        pratica_id: form.pratica_id || undefined,
      });
      const nuove = (data.items || []).map((v) => ({ titolo: "", descrizione: v.descrizione, onorario: 0, spese: 0 }));
      if (!nuove.length) { toast.info("Nessuna prestazione suggerita"); return; }
      setForm((prev) => {
        const voci = [...(prev.voci || [])];
        const primaVuota = voci.length === 1 && !voci[0].titolo && !voci[0].descrizione && !voci[0].onorario && !voci[0].spese;
        return { ...prev, voci: primaVuota ? nuove : [...voci, ...nuove] };
      });
      toast.success(`${nuove.length} prestazioni generate dall'IA · rivedi gli importi`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Errore IA");
    } finally { setAiLoading(false); }
  };

  const addVoceFromTemplate = (vt) => {
    setForm((prev) => {
      // Se la prima voce è vuota, sovrascrivi; altrimenti appendi
      const voci = [...(prev.voci || [])];
      const isFirstEmpty = voci.length === 1 && !voci[0].titolo && !voci[0].descrizione && !voci[0].onorario && !voci[0].spese;
      const newVoce = {
        titolo: vt.titolo || "",
        descrizione: vt.descrizione || "",
        onorario: vt.onorario || 0,
        spese: vt.spese || 0,
      };
      if (isFirstEmpty) voci[0] = newVoce;
      else voci.push(newVoce);
      return { ...prev, voci };
    });
    toast.success(`Voce "${vt.titolo}" aggiunta`);
  };

  const setVoce = (i, key, val) => {
    setForm((prev) => {
      const voci = [...(prev.voci || [])];
      voci[i] = { ...voci[i], [key]: val };
      return { ...prev, voci };
    });
  };

  const addVoce = () => setForm((prev) => ({ ...prev, voci: [...(prev.voci || []), emptyVoce()] }));

  const delVoce = (i) => setForm((prev) => {
    const voci = [...(prev.voci || [])];
    voci.splice(i, 1);
    return { ...prev, voci: voci.length ? voci : [emptyVoce()] };
  });

  const submit = async (e) => {
    e.preventDefault();
    const tot = computeTotali(form);
    const voci = (form.voci || []).map((v) => ({
      titolo: v.titolo || "",
      descrizione: v.descrizione || "",
      onorario: parseFloat(v.onorario) || 0,
      spese: parseFloat(v.spese) || 0,
    }));
    const payload = {
      ...form,
      voci,
      cassa_pct: parseFloat(form.cassa_pct) || 0,
      spese_forfait: parseFloat(form.spese_forfait) || 0,
      spese_esenti: parseFloat(form.spese_esenti) || 0,
      marca_bollo: parseFloat(form.marca_bollo) || 0,
      importo: tot.totale,
      cliente_id: form.cliente_id || null,
      altri_clienti_ids: (form.altri_clienti_ids || []).filter((id) => id && id !== form.cliente_id),
    };
    try {
      if (editing) await api.put(`/preventivi/${editing.id}`, payload);
      else await api.post("/preventivi", payload);
      toast.success("Salvato");
      setOpen(false); setEditing(null); load();
    } catch { toast.error("Errore"); }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyPrev());
    setSelectedMacroarea("");
    setOpen(true);
    // Precompila numero automaticamente
    api.get("/preventivi/next-numero").then((r) => {
      setForm((prev) => ({ ...prev, numero: r.data.numero || prev.numero }));
    }).catch(() => {});
  };
  useNuovoParam(openNew);
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      numero: p.numero || "", data: p.data?.slice(0, 10) || "", cliente_id: p.cliente_id || "", altri_clienti_ids: p.altri_clienti_ids || [],
      tipo_pratica: p.tipo_pratica || "generica", descrizione: p.descrizione || "",
      voci: (p.voci && p.voci.length > 0) ? p.voci.map((v) => ({ titolo: v.titolo || "", descrizione: v.descrizione || "", onorario: v.onorario || 0, spese: v.spese || 0 })) : [{ titolo: "", descrizione: p.descrizione || "", onorario: p.importo || 0, spese: 0 }],
      cassa_pct: p.cassa_pct ?? 5.0,
      spese_forfait: p.spese_forfait || 0,
      spese_esenti: p.spese_esenti || 0,
      marca_bollo: p.marca_bollo ?? 2,
      banca: p.banca || "",
      intestatario_conto: p.intestatario_conto || "",
      iban: p.iban || "",
      importo: p.importo || 0,
      stato: p.stato || "bozza",
      note: p.note || "",
    });
    setSelectedMacroarea(macroareaForTipo(p.tipo_pratica || "generica"));
    setOpen(true);
  };
  const apriId = new URLSearchParams(window.location.search).get("apri");
  useEffect(() => {
    if (!apriId || !items.length) return;
    const el = items.find((x) => x.id === apriId);
    if (el) { openEdit(el); window.history.replaceState({}, "", window.location.pathname); }
  }, [apriId, items]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (id) => {
    if (!window.confirm("Eliminare preventivo?")) return;
    try { await api.delete(`/preventivi/${id}`); toast.success("Eliminato"); load(); } catch { toast.error("Errore"); }
  };

  const duplica = async (p) => {
    if (!window.confirm(`Duplicare il preventivo ${p.numero}? Verrà creata una copia in stato bozza con nuovo numero e data di oggi.`)) return;
    try {
      const { data } = await api.post(`/preventivi/${p.id}/duplica`);
      toast.success(`Copia creata: ${data.numero}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella duplicazione"); }
  };

  const downloadPdf = async (p) => {
    try {
      await apiDownload(`/preventivi/${p.id}/pdf`, `Preventivo_${p.numero}.pdf`);
      toast.success("Download avviato");
    } catch { toast.error("Errore PDF"); }
  };

  const openAccetta = (p) => {
    setUploadDialog(p);
    setUploadFile(null);
  };

  const creaIncaricoOrfano = async (p) => {
    if (!window.confirm(`Creare ora l'incarico per il preventivo ${p.numero}?`)) return;
    try {
      const { data } = await api.post(`/preventivi/${p.id}/crea-incarico`);
      toast.success(data.already_existed ? "Incarico già presente, collegato" : "Incarico creato con successo");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione incarico");
    }
  };

  const accettaTablet = async (dataUrl) => {
    setUploadLoading(true);
    try {
      const { data } = await api.post(`/preventivi/${uploadDialog.id}/accetta-tablet`, {
        firma_cliente: dataUrl, firma_data: new Date().toISOString().slice(0, 10),
      });
      toast.success("Preventivo firmato: incarico creato!");
      setUploadDialog(null);
      load();
      setTimeout(() => navigate(`/pratiche/${data.pratica_id}`), 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore accettazione");
    }
    setUploadLoading(false);
  };

  const submitAccetta = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error("Seleziona un file"); return; }
    setUploadLoading(true);
    const fd = new FormData();
    fd.append("file", uploadFile);
    try {
      const { data } = await api.post(`/preventivi/${uploadDialog.id}/accetta`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Incarico creato!");
      setUploadDialog(null);
      setUploadFile(null);
      load();
      setTimeout(() => navigate(`/pratiche/${data.pratica_id}`), 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore accettazione");
    }
    setUploadLoading(false);
  };

  return (
    <div className="p-4 md:p-8 w-full max-w-[1400px] mx-auto" data-testid="preventivi-page">
      <TabsPreventiviAvvisi attiva="preventivi" />
      <PageHeader
        eyebrow="Preventivi & Avvisi"
        title="Preventivi"
        description="Crea, scarica in PDF e trasforma i preventivi accettati in incarichi con un click."
        actions={<div className="flex items-center gap-2">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-1" data-testid="view-toggle">
          <button onClick={() => changeViewMode("cards")} data-testid="view-cards" className={`px-2.5 py-1.5 rounded text-xs font-medium inline-flex items-center gap-1.5 ${viewMode === "cards" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}><SquaresFour size={14} /> Schede</button>
          <button onClick={() => changeViewMode("list")} data-testid="view-list" className={`px-2.5 py-1.5 rounded text-xs font-medium inline-flex items-center gap-1.5 ${viewMode === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}><ListIcon size={14} /> Lista</button>
        </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-preventivo-button"><Plus size={16} weight="bold" /> Nuovo preventivo</Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Modifica preventivo" : "Nuovo preventivo"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2"><Label>Numero</Label><Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} required data-testid="preventivo-numero-input" /></div>
                  <div className="space-y-2"><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} required /></div>
                  <div className="space-y-2">
                    <Label>Stato</Label>
                    <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bozza">Bozza</SelectItem>
                        <SelectItem value="inviato">Inviato</SelectItem>
                        <SelectItem value="accettato">Accettato</SelectItem>
                        <SelectItem value="rifiutato">Rifiutato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Cliente</Label>
                    <ClientePicker clienti={clienti} value={form.cliente_id} onChange={(v) => setForm({ ...form, cliente_id: v })} onCreato={(c) => setClienti((l) => [...l, c])} testId="preventivo-cliente-picker" />
                  </div>
                  <div className="space-y-2">
                    <Label>Macroarea</Label>
                    <Select value={selectedMacroarea} onValueChange={(v) => { setSelectedMacroarea(v); setForm((prev) => ({ ...prev, tipo_pratica: "" })); }}>
                      <SelectTrigger data-testid="preventivo-macroarea"><SelectValue placeholder="Prima scegli macroarea" /></SelectTrigger>
                      <SelectContent>
                        {macroaree.map((m) => <SelectItem key={m.slug} value={m.slug}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo incarico</Label>
                    <Select value={form.tipo_pratica} onValueChange={onTipoChange} disabled={!selectedMacroarea}>
                      <SelectTrigger data-testid="preventivo-tipo-select"><SelectValue placeholder={selectedMacroarea ? "Scegli il tipo" : "—"} /></SelectTrigger>
                      <SelectContent>
                        {tipi.filter((t) => (t.macroarea || "altro") === selectedMacroarea).map((t) => (
                          <SelectItem key={t.id} value={t.tipo}>
                            {t.label}{t.prezzo_standard > 0 ? ` · ${fmt(t.prezzo_standard)}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <AltriClientiPicker clienti={clienti} onCreato={(c) => setClienti((l) => [...l, c])} principaleId={form.cliente_id} value={form.altri_clienti_ids || []} onChange={(ids) => setForm({ ...form, altri_clienti_ids: ids })} />
                <div className="space-y-2"><Label>Oggetto</Label><Textarea value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} rows={2} required placeholder="Prestazioni professionali relative a pratica edilizia per..." /></div>

                {/* Voci multiple */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Label>Voci del preventivo</Label>
                    <div className="flex items-center gap-2">
                      {tipi.length > 0 && (
                        <Select value="" onValueChange={(idx) => { const t = tipi[parseInt(idx)]; if (t) addVoceDaTipo(t); }}>
                          <SelectTrigger className="h-8 text-xs w-64" data-testid="voce-tipo-picker"><SelectValue placeholder={`+ Aggiungi voce dai Tipi Incarico (${tipi.length})`} /></SelectTrigger>
                          <SelectContent>
                            {tipi.map((t, idx) => (
                              <SelectItem key={t.id} value={String(idx)} data-testid={`voce-tipo-option-${t.tipo}`}>
                                {t.label}{(t.prezzo_standard > 0 || t.spese_standard > 0) ? ` · ${fmt((t.prezzo_standard || 0) + (t.spese_standard || 0))}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Button type="button" variant="outline" size="sm" onClick={addVoce} data-testid="add-voce-button"><Plus size={12} /> Vuota</Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-end mb-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={generaPrestazioniAI}
                      disabled={aiLoading}
                      data-testid="prestazioni-ai-btn"
                      className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900"
                    >
                      <Sparkle size={13} weight="fill" className="mr-1.5 text-amber-500" />
                      {aiLoading ? "Generazione..." : "Genera prestazioni con IA"}
                    </Button>
                  </div>
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-xs" data-testid="voci-table">
                      <thead className="bg-slate-900 text-white">
                        <tr>
                          <th className="text-left p-2 w-8">#</th>
                          <th className="text-left p-2">Titolo &amp; Descrizione</th>
                          <th className="text-right p-2 w-28">Onorario €</th>
                          <th className="text-right p-2 w-28">Spese €</th>
                          <th className="text-right p-2 w-28">Parziale</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(form.voci || []).map((v, i) => (
                          <tr key={i} className="border-t border-slate-100" data-testid={`voce-row-${i}`}>
                            <td className="p-2 font-mono text-slate-500 align-top">{String(i + 1).padStart(2, "0")}</td>
                            <td className="p-1 space-y-1">
                              <Input
                                value={v.titolo}
                                onChange={(e) => setVoce(i, "titolo", e.target.value)}
                                placeholder="Titolo (es. CILA)"
                                className="text-xs font-semibold uppercase"
                                data-testid={`voce-titolo-${i}`}
                              />
                              <Textarea
                                value={v.descrizione}
                                onChange={(e) => setVoce(i, "descrizione", e.target.value)}
                                rows={2}
                                className="text-xs min-h-[40px]"
                                placeholder="Descrizione dettagliata..."
                                data-testid={`voce-descrizione-${i}`}
                              />
                            </td>
                            <td className="p-1 align-top">
                              <Input type="number" step="0.01" value={v.onorario} onChange={(e) => setVoce(i, "onorario", e.target.value)} className="text-xs text-right font-mono" data-testid={`voce-onorario-${i}`} />
                            </td>
                            <td className="p-1 align-top">
                              <Input type="number" step="0.01" value={v.spese} onChange={(e) => setVoce(i, "spese", e.target.value)} className="text-xs text-right font-mono" data-testid={`voce-spese-${i}`} />
                            </td>
                            <td className="p-2 text-right font-mono tabular-nums align-top">{fmt((parseFloat(v.onorario) || 0) + (parseFloat(v.spese) || 0))}</td>
                            <td className="p-1 text-center align-top">
                              <Button type="button" variant="ghost" size="sm" onClick={() => delVoce(i)} className="h-7 w-7 p-0" data-testid={`voce-del-${i}`}><Trash size={12} className="text-red-600" /></Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totali + Cassa/Bollo */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="space-y-1"><Label className="text-xs">Spese esenti IVA € <span className="text-[10px] text-slate-400">art. 15</span></Label><Input type="number" step="0.01" min="0" value={form.spese_esenti ?? 0} onChange={(e) => setForm({ ...form, spese_esenti: e.target.value })} className="font-mono" data-testid="preventivo-spese-esenti-input" /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1"><Label className="text-xs">Cassa %</Label><Input type="number" step="0.01" value={form.cassa_pct} onChange={(e) => setForm({ ...form, cassa_pct: e.target.value })} className="font-mono" data-testid="preventivo-cassa-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">Spese forfait €</Label><Input type="number" step="0.01" value={form.spese_forfait} onChange={(e) => setForm({ ...form, spese_forfait: e.target.value })} className="font-mono" /></div>
                      <div className="space-y-1"><Label className="text-xs">Marca da Bollo €</Label><Input type="number" step="0.01" value={form.marca_bollo} onChange={(e) => setForm({ ...form, marca_bollo: e.target.value })} className="font-mono" data-testid="preventivo-bollo-input" /></div>
                    </div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm" data-testid="preventivo-totali-preview">
                    {(() => {
                      const t = computeTotali(form);
                      return (
                        <div className="space-y-1 font-mono tabular-nums text-xs">
                          <div className="flex justify-between"><span className="text-slate-500">Sommano onorario</span><span>{fmt(t.tot_onorario)}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Cassa {(parseFloat(form.cassa_pct) || 0).toFixed(0)}%</span><span>{fmt(t.cassa_val)}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Spese{t.spese_esenti ? <span className="text-[10px]"> (di cui esenti IVA {fmt(t.spese_esenti)})</span> : null}</span><span>{fmt(t.tot_spese + t.spese_esenti)}</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Marca da Bollo</span><span>{fmt(t.marca_bollo)}</span></div>
                          <div className="flex justify-between pt-1 mt-1 border-t border-slate-300 font-bold text-sm"><span>TOTALE V/S DARE</span><span data-testid="preventivo-totale-display">{fmt(t.totale)}</span></div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <AltriDettagli aperto={!!(form.banca || form.iban || form.intestatario_conto || form.note)} riassunto="coordinate bancarie, note sotto la firma" testId="preventivo-altri-dettagli">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Coordinate bancarie (opzionali)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label>Banca di Appoggio</Label><Input value={form.banca} onChange={(e) => setForm({ ...form, banca: e.target.value })} placeholder="Es. Intesa Sanpaolo — filiale di..." /></div>
                    <div className="space-y-1"><Label>Intestatario</Label><Input value={form.intestatario_conto} onChange={(e) => setForm({ ...form, intestatario_conto: e.target.value })} placeholder="Se diverso dallo Studio" /></div>
                  </div>
                  <div className="space-y-1"><Label>Codice IBAN</Label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase().replace(/\s/g, "") })} placeholder="IT60X0542811101000000123456" className="font-mono" data-testid="preventivo-iban-input" /></div>
                </div>

                <div className="space-y-2"><Label>Note aggiuntive</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="Eventuali note che appariranno sotto la firma del preventivo." /></div>
                </AltriDettagli>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                  <Button type="submit" data-testid="preventivo-submit-button">Salva</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>}
      />

      {viewMode === "cards" ? (
        items.length === 0 ? (
          <Card className="p-12 text-center border-slate-200 shadow-none"><div className="text-sm text-slate-500">Nessun preventivo ancora. Clicca &quot;Nuovo preventivo&quot; per iniziare.</div></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="preventivi-cards">
            {items.map((p) => {
              const voci = p.voci || [];
              const netto = voci.reduce((s, v) => s + (Number(v.onorario) || 0), 0);
              const speseVoci = voci.reduce((s, v) => s + (Number(v.spese) || 0), 0);
              const spese = speseVoci + (Number(p.spese_forfait) || 0) + (Number(p.spese_esenti) || 0);
              const totale = netto + spese;
              const macro = macroareaLabel(macroareaForTipo(p.tipo_pratica || "generica"));
              const dataLabel = p.data ? new Date(p.data).toLocaleDateString("it-IT") : "—";
              return (
                <Card key={p.id} className="border-slate-200 shadow-none hover:shadow-md hover:border-slate-300 transition-all flex flex-col" data-testid={`preventivo-card-${p.id}`}>
                  <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100">
                    <div className="flex items-center gap-1.5"><StatusBadge status={p.stato} />{p.pratica_id && <span className="text-[10px] text-emerald-700 inline-flex items-center gap-0.5"><CheckCircle size={10} weight="fill" /> Incarico</span>}{p.stato === "accettato" && !p.pratica_id && <span className="text-[10px] font-mono uppercase bg-amber-100 text-amber-800 border border-amber-300 rounded px-1 py-0.5">⚠ Senza incarico</span>}</div>
                    <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-blue-600 p-1 rounded" data-testid={`card-edit-${p.id}`} title="Modifica"><PencilSimple size={13} /></button>
                  </div>
                  <div className="p-4 flex-1 flex flex-col gap-2">
                    <div className="flex items-center gap-2"><User size={14} className="text-slate-400 flex-shrink-0" /><div className="font-heading text-base font-bold text-slate-900 truncate uppercase" data-testid={`preventivo-cliente-${p.id}`}>{nomiCommittenti(clienti, p)}</div></div>
                    {p.descrizione && <div className="text-sm text-slate-700 line-clamp-2">{p.descrizione}</div>}
                    <div className="flex items-center gap-1.5 flex-wrap"><span className="text-[10px] font-mono uppercase tracking-wider text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5">{macro}</span><span className="text-[10px] font-mono uppercase tracking-wider text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">{tipoLabel(p.tipo_pratica || "generica")}</span></div>
                    <div className="grid grid-cols-2 gap-2 pt-2 mt-auto border-t border-slate-100">
                      <div><div className="text-[10px] font-mono uppercase text-slate-400">N° / Data</div><div className="text-xs font-mono text-slate-700">{p.numero} · {dataLabel}</div></div>
                      <div className="text-right"><div className="text-[10px] font-mono uppercase text-slate-400">Totale</div><div className="text-sm font-mono tabular-nums font-semibold text-slate-900">{fmt(totale)}</div></div>
                    </div>
                  </div>
                  <div className="border-t border-slate-100 flex items-center justify-around px-2 py-1">
                    <button onClick={() => downloadPdf(p)} className="p-1.5 text-slate-500 hover:text-blue-600" title="Scarica PDF" data-testid={`pdf-preventivo-${p.id}`}><DownloadSimple size={14} /></button>
                    {p.stato === "accettato" && !p.pratica_id && <button onClick={() => openAccetta(p)} className="p-1.5 text-slate-500 hover:text-emerald-600" title="Carica firmato & crea incarico"><FileArrowUp size={14} /></button>}
                    {p.pratica_id && <button onClick={() => navigate(`/pratiche/${p.pratica_id}#lettera`)} className="p-1.5 text-slate-500 hover:text-emerald-600" title="Lettera d'incarico e privacy" data-testid={`lettera-preventivo-${p.id}`}><FileText size={14} /></button>}
                    <button onClick={() => duplica(p)} className="p-1.5 text-slate-500 hover:text-blue-600" title="Duplica" data-testid={`duplica-preventivo-${p.id}`}><Copy size={14} /></button>
                    <button onClick={() => del(p.id)} className="p-1.5 text-slate-500 hover:text-red-600" title="Elimina" data-testid={`delete-preventivo-${p.id}`}><Trash size={14} /></button>
                  </div>
                </Card>
              );
            })}
          </div>
        )
      ) : (
      <Card className="border-slate-200 shadow-none overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">Nessun preventivo ancora. Clicca &quot;Nuovo preventivo&quot; per iniziare.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed" style={{ minWidth: 1100 }}>
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  
<th className="w-[110px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">
Numero</th>
                  
<th className="w-[100px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">
Data</th>
                  
<th className="w-[130px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">
Macroarea</th>
                  
<th className="w-[130px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">
Tipo</th>
                  
<th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">
Cliente</th>
                  
<th className="w-[120px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">
Stato</th>
                  
<th className="w-[110px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 text-right">
Netto</th>
                  
<th className="w-[100px] p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 text-right">
Spese</th>
                  
<th className="w-[120px] p-3">
</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((p) => {
                  const voci = p.voci || [];
                  const netto = voci.reduce((s, v) => s + (Number(v.onorario) || 0), 0);
                  const speseVoci = voci.reduce((s, v) => s + (Number(v.spese) || 0), 0);
                  const spese = speseVoci + (Number(p.spese_forfait) || 0) + (Number(p.spese_esenti) || 0);
                  return (
                  <tr key={p.id} className="hover:bg-slate-50" data-testid={`preventivo-row-${p.id}`}>
                    <td className="p-3 font-mono font-medium">{p.numero}</td>
                    <td className="p-3 font-mono text-xs text-slate-600">{new Date(p.data).toLocaleDateString("it-IT")}</td>
                    <td className="p-3">
                      <div className="text-sm font-semibold text-slate-900" data-testid={`macroarea-${p.id}`}>{macroareaLabel(macroareaForTipo(p.tipo_pratica || "generica"))}</div>
                      {p.descrizione && <div className="text-[11px] text-slate-500 truncate max-w-[220px]" title={p.descrizione}>{p.descrizione}</div>}
                    </td>
                    <td className="p-3 text-slate-700 text-sm" data-testid={`tipo-${p.id}`}>{tipoLabel(p.tipo_pratica || "generica")}</td>
                    <td className="p-3 font-semibold text-slate-900 text-sm uppercase" data-testid={`preventivo-cliente-row-${p.id}`}>{nomiCommittenti(clienti, p)}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <StatusBadge status={p.stato} />
                        {p.pratica_id && <span className="text-[10px] text-emerald-700 inline-flex items-center gap-0.5"><CheckCircle size={10} weight="fill" /> Incarico</span>}
                        {p.stato === "accettato" && !p.pratica_id && (
                          <span className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 font-mono uppercase" title="Preventivo accettato ma incarico non ancora creato" data-testid={`orfano-badge-${p.id}`}>
                            ⚠ Senza incarico
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-right font-mono text-xs tabular-nums" data-testid={`netto-${p.id}`}>{netto > 0 ? fmt(netto) : "—"}</td>
                    <td className="p-3 text-right font-mono text-xs tabular-nums text-slate-500" data-testid={`spese-${p.id}`}>{spese > 0 ? fmt(spese) : "—"}</td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => downloadPdf(p)} title="Scarica PDF" data-testid={`pdf-preventivo-${p.id}`}>
                          <DownloadSimple size={14} />
                        </Button>
                        {!p.pratica_id && (
                          <Button variant="ghost" size="sm" onClick={() => openAccetta(p)} title="Carica firmato & crea incarico" data-testid={`accetta-preventivo-${p.id}`}>
                            <FileArrowUp size={14} className="text-emerald-700" />
                          </Button>
                        )}
                        {p.stato === "accettato" && !p.pratica_id && (
                          <Button variant="ghost" size="sm" onClick={() => creaIncaricoOrfano(p)} title="Crea incarico ora" data-testid={`crea-incarico-${p.id}`}>
                            <span className="text-xs font-mono uppercase font-bold text-amber-800">Crea</span>
                          </Button>
                        )}
                        {p.pratica_id && (
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/pratiche/${p.pratica_id}`)} title="Vai all'incarico">
                            <PaperPlaneTilt size={14} />
                          </Button>
                        )}
                        {p.stato === "accettato" && (
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/fatturazione?da_preventivo=${p.id}`)} title="Crea fattura con voci trasferite" data-testid={`crea-fattura-${p.id}`}>
                            <CurrencyEur size={14} className="text-blue-700" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p)}><PencilSimple size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => duplica(p)} title="Duplica" data-testid={`duplica-preventivo-row-${p.id}`}><Copy size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => del(p.id)}><Trash size={14} className="text-red-600" /></Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}

      <SignaturePad
        open={padOpen}
        onOpenChange={setPadOpen}
        titolo="Firma per accettazione del preventivo"
        sottotitolo="Passa il tablet al cliente: la firma verrà inserita nel PDF del preventivo, archiviato come documento dell'incarico."
        onConfirm={accettaTablet}
      />

      {/* Accetta dialog */}
      <Dialog open={!!uploadDialog} onOpenChange={(o) => { if (!o) setUploadDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Preventivo accettato — crea incarico</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAccetta} className="space-y-4">
            <div className="text-sm text-slate-600">
              Fai firmare il cliente sul tablet oppure carica il preventivo già firmato. Verrà creato automaticamente un <strong>Incarico</strong> con lo stesso tipo, importo e cliente.
            </div>
            <Button type="button" variant="outline" className="w-full h-14 border-dashed" onClick={() => setPadOpen(true)} disabled={uploadLoading} data-testid="accetta-firma-tablet-btn">
              <PenNib size={16} /> Firma su tablet e crea incarico
            </Button>
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">oppure carica il file</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="space-y-2">
              <Label>Preventivo firmato (PDF/JPG/PNG)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-slate-900 file:text-white hover:file:bg-slate-700 cursor-pointer"
                data-testid="accetta-file-input"
              />
              {uploadFile && <div className="text-xs text-slate-500 font-mono">{uploadFile.name} · {(uploadFile.size / 1024).toFixed(1)} KB</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUploadDialog(null)}>Annulla</Button>
              <Button type="submit" disabled={uploadLoading || !uploadFile} data-testid="accetta-submit-button">
                {uploadLoading ? "Creazione..." : "Crea incarico"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
